'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useProduct } from '@/screens/Product/hooks/useProduct'
import { CrossIcon } from '@/screens/services-trade-in/page/ui/trade-in-form/assets/cross-icon'

import { useRegion } from '@/entities/regions'
import { fetchAllStores } from '@/entities/shops'
import {
	getValuesAfterPropertyChange,
	sortTradeInProperties,
	TradeinDevice,
	TradeInPropertySelect,
	useGetBaseCategoriesQuery,
	useGetCategoriesQuery,
	useGetProductTradeInDataQuery,
	useLazyGetTradeInPriceQuery
} from '@/entities/trade-in'
import { useUser } from '@/entities/user'

import ErrorIcon from '@/shared/assets/icons/alert-circle.svg'
import SuccessIcon from '@/shared/assets/icons/circle-check.svg'
import QuestionIcon from '@/shared/assets/icons/question.svg'
import { API_URL, BASE_URL } from '@/shared/lib/const/env'
import { formatPrice } from '@/shared/lib/utils/price/format-price'
import { normalizeEmail } from '@/shared/lib/validation/email'
import { formatPhoneMasked, normalizePhone } from '@/shared/lib/validation/phone'
import { TShop } from '@/shared/types/product-types'
import { Button, Input, Modal, RHFEmailInput, RHFPhoneInput, Select, Tooltip } from '@/shared/ui'
import { AgreementBlock } from '@/shared/ui/AgreementBlock/AgreementBlock'
import { Icon } from '@/shared/ui/icon'
import { Image } from '@/shared/ui/Image'
import { SegmentedControl } from '@/shared/ui/segmented-control'
import { Skeletons, SkeletonType } from '@/shared/ui/Skeletons/Skeleton'

import { Price } from '../../../product-panel/components/product-price-widget/PricesBlock/Price/Price'

import { submitTradeInMindboxRegister } from './api/tradein-api'

import cl from './trade-in-form.module.scss'

type FLPFormProps = {
	isOpened: boolean
	setIsModalOpen: (state: boolean) => void
}

type TradeInFormData = {
	FIO: string
	EMAIL: string
	PHONE: string
	DELIVERY: string
	PAYMENT: string
}

const formMessage = (status: number): JSX.Element => {
	switch (status) {
		case 201:
			return (
				<div className={cl.form__success}>
					<div className={cl.form__text}>
						<div className={cl.form__icon}>
							<Icon icon={ErrorIcon} aria-label="icon" width={40} height={40} />
						</div>

						<h2>Что-то пошло не так</h2>
						<p>Не удалось отправить заявку, попробуйте позже</p>
					</div>
				</div>
			)

		case 200:
			return (
				<div className={cl.form__success}>
					<div className={cl.form__text}>
						<div className={cl.form__icon}>
							<Icon icon={SuccessIcon} aria-label="icon" width={44} height={44} />
						</div>
						<h2>Заявка успешно отправлена</h2>
						<p>Наши операторы скоро позвонят вам для подтверждения</p>
					</div>
				</div>
			)
		default:
			return (
				<div className={cl.form__success}>
					<div className={cl.form__text}>
						<div className={cl.iconWrap}>
							<Icon icon={ErrorIcon} aria-label="icon" width={40} height={40} />
						</div>
						<h2>Произошла ошибка, попробуйте позже</h2>
					</div>
				</div>
			)
	}
}

type ExtendedTShop = TShop & {
	B24_ID: string
}

const paymentOptions = [
	{ label: 'Наличными', value: 'Наличными' },
	{ label: 'Картой', value: 'Картой' },
	{ label: 'По СБП', value: 'По СБП' },
	{ label: 'Рассрочка/Кредит', value: 'Рассрочка/Кредит' }
]

const SECTION_CATEGORY_CODES: Record<string, string[]> = {
	'103': ['iphone'],
	'77': ['mac', 'macbook']
}

const getTradeInProductPrice = (productPrice: number, tradeInPrice: number): number =>
	Math.max(productPrice - tradeInPrice, 1)

interface deliveryOptionsProps {
	label: string
	value: string
	id?: string
}

const submitTradeInForm = async (formData: Record<string, unknown>): Promise<any> => {
	try {
		const response = await fetch(`${API_URL}/b24-element-add`, {
			method: 'POST',
			body: JSON.stringify(formData),
			headers: {
				'Content-Type': 'application/json'
			},
			credentials: 'include'
		})

		const data = await response.json()

		// Return response with status code to match the original api.post behavior
		return {
			code: response.status,
			data: data
		}
	} catch (error) {
		// Handle network errors
		return {
			code: 0,
			data: null
		}
	}
}

export const TradeInForm = ({ isOpened, setIsModalOpen }: FLPFormProps) => {
	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors }
	} = useForm<TradeInFormData>({
		mode: 'onBlur',
		defaultValues: {
			FIO: '',
			EMAIL: '',
			PHONE: '',
			DELIVERY: '',
			PAYMENT: ''
		}
	})

	const [shopsList, setShopsList] = useState<deliveryOptionsProps[]>([])
	const { product } = useProduct()
	const { currentRegion } = useRegion()
	const [isLoading, setIsLoading] = useState(false)
	const [formResponse, setFormResponse] = useState<JSX.Element>(null)
	const [tradeInPriceFromValues, setTradeInPriceFromValues] = useState<TradeInPriceFormValues | null>(null)
	const { user, isLoading: userInfoIsLoading } = useUser()
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const search = searchParams.toString()
	const asPath = `${pathname}${search ? `?${search}` : ''}`
	const pageUrl = `https://www.iport.ru` + asPath

	const { data: productTradeInData } = useGetProductTradeInDataQuery(Number(product.ID))

	const initialTradeInValues = useMemo((): Record<string, string> | undefined => {
		if (!productTradeInData) return undefined
		const {
			TRADEIN_LINEYKA,
			VSTROENNAYA_PAMYAT,
			TRADEIN_TYPE_SIM,
			TRADEIN_SERIYA_PROTSESSORA,
			TRADEIN_COLOR,
			DIAGONAL
		} = productTradeInData
		return {
			TRADEIN_LINEYKA,
			VSTROENNAYA_PAMYAT,
			TRADEIN_TYPE_SIM,
			TRADEIN_SERIYA_PROTSESSORA,
			TRADEIN_COLOR,
			DIAGONAL
		}
	}, [productTradeInData])

	const baseTradeInPrice = useMemo(
		() =>
			productTradeInData?.PRICE
				? getTradeInProductPrice(product.PRICE.VALUE, productTradeInData.PRICE)
				: product.PRICE.VALUE,
		[productTradeInData?.PRICE, product.PRICE.VALUE]
	)

	const [price, setPrice] = useState<number>(product.PRICE.VALUE)

	useEffect(() => {
		if (!tradeInPriceFromValues) {
			setPrice(baseTradeInPrice)
		}
	}, [baseTradeInPrice, tradeInPriceFromValues])
	const getDeliveryOptions = (stores: ExtendedTShop[]): deliveryOptionsProps[] => {
		const options = stores
			.filter(store => store.UF_IS_SHOP === '1')
			.filter(({ REGION }) => REGION === currentRegion.REGION_NAME)
			.map(store => {
				const name = store.NAME.replace('Авторизованный фирменный магазин ', 'Самовывоз из ')
				return {
					label: name,
					value: name,
					id: store?.B24_ID ? store?.B24_ID : ``
				}
			})

		return [...options]
	}

	useEffect(() => {
		const fetchStores = async () => {
			try {
				const storesData = await fetchAllStores()
				if (storesData) setShopsList(getDeliveryOptions(storesData))
			} catch (error) {
				console.error('Ошибка при загрузке магазинов:', error)
			}
		}

		fetchStores()
	}, [])

	useEffect(() => {
		if (!user) return

		setValue('FIO', user.first_name)
		setValue('PHONE', formatPhoneMasked(user.user_phone))

		if (user.user_email && user.user_email !== '0') {
			setValue('EMAIL', normalizeEmail(user.user_email))
		}
	}, [setValue, user])

	const onFinish = async (values: TradeInFormData): Promise<void> => {
		if (!tradeInPriceFromValues) return
		setIsLoading(true)
		try {
			const { FIO, EMAIL, PHONE, DELIVERY, PAYMENT } = values
			// В заявку уходит итоговая цена с учётом выкупа и гарантированной выгоды
			const { ADDITIONAL_DISCOUNT, ...tradeInApplicationValues } = tradeInPriceFromValues
			const calculatedTradeInPrice = product.PRICE.VALUE - tradeInPriceFromValues.TRADE_IN_PRICE - ADDITIONAL_DISCOUNT
			const eventName = `Сдайте своё старое устройство и получите скидку на новое`

			// Если выбран самовывоз, находим соответствующий B24_ID
			const store = DELIVERY !== '27827' ? shopsList.find(item => item.value === DELIVERY) : null

			const payload = {
				FIO,
				EMAIL,
				PHONE: normalizePhone(PHONE),
				PAY_TYPE: PAYMENT,
				PRODUCT_CODE: product.KOD,
				STORE: store ? store.id : '',
				PAGE: pageUrl,
				TYPE: `TradeIn`,
				...tradeInApplicationValues
			}

			const response = await submitTradeInForm(payload)

			if (response.code === 200) {
				void submitTradeInMindboxRegister({
					NAME: FIO,
					PHONE: normalizePhone(PHONE),
					EMAIL,
					TITLE: eventName,
					URL: pageUrl,
					ADDITIONAL_INFO: tradeInPriceFromValues.TRADE_IN_ITEM,
					CALLING_FORM: product.TITLE,
					SEND_TEXT: DELIVERY,
					SUM: calculatedTradeInPrice
				}).catch(error => {
					console.error('Ошибка отправки trade-in mindbox register:', error)
				})
			}

			setFormResponse(formMessage(response.code))
		} catch (e) {
			console.error(`Произошла ошибка:`, e)
			setFormResponse(formMessage(0))
		}
		setIsLoading(false)
	}

	const onClose = () => setIsModalOpen(false)

	return (
		<Modal isOpen={isOpened} onClose={onClose} className={cl.modal}>
			{!formResponse ? (
				<>
					<h2 className={cl.title}>Сдайте своё старое устройство и получите скидку на новое </h2>
					<p className={cl.text}>
						Рассчитайте выгоду и оставьте заявку. Программа обмена в Трейд-ин действует в любом нашем{' '}
						<Link prefetch={false} href="/shops" className={cl.link} target="_blank">
							магазине
						</Link>
					</p>

					<div className={cl.product}>
						<Image src={BASE_URL + product.IMAGES[0]} width={80} height={80} alt="" className={cl.product__image} />
						<div className={cl.product__content}>
							<h3 className={cl.product__title}>{product.TITLE}</h3>
							<div className={cl.product__price}>
								<div>
									от <Price price={price} className={cl.mainPrice} />
								</div>
								<p>по обмену в Трейд-ин</p>
							</div>
						</div>
					</div>

					{tradeInPriceFromValues && (
						<div className={cl.priceBreakdown}>
							<div className={cl.priceBreakdown__row}>
								<span className={cl.priceBreakdown__label}>Цена без скидки</span>
								<hr className={cl.priceBreakdown__leader} />
								<span className={clsx(cl.priceBreakdown__value, cl.priceBreakdown__value_old)}>
									{formatPrice(product.PRICE.VALUE)}
								</span>
							</div>

							{tradeInPriceFromValues.ADDITIONAL_DISCOUNT !== 0 && (
								<div className={cl.priceBreakdown__row}>
									<span className={cl.priceBreakdown__label}>
										Гарантированная выгода
										<button
											type="button"
											className={cl.priceBreakdown__info}
											data-tooltip-id="tradein-guaranteed-benefit"
											aria-label="Подробнее о гарантированной выгоде">
											<Icon icon={QuestionIcon} width={16} height={16} aria-hidden />
										</button>
									</span>
									<hr className={cl.priceBreakdown__leader} />
									<span className={clsx(cl.priceBreakdown__value, cl.priceBreakdown__value_benefit)}>
										−{formatPrice(tradeInPriceFromValues.ADDITIONAL_DISCOUNT)}
									</span>
								</div>
							)}

							<div className={cl.priceBreakdown__row}>
								<span className={cl.priceBreakdown__label}>Выгода по Трейд-ин</span>
								<hr className={cl.priceBreakdown__leader} />
								<span className={clsx(cl.priceBreakdown__value, cl.priceBreakdown__value_benefit)}>
									−{formatPrice(tradeInPriceFromValues.TRADE_IN_PRICE)}
								</span>
							</div>

							<Tooltip id="tradein-guaranteed-benefit" clickable variant="light" opacity={1} className={cl.tooltip}>
								подробности в{' '}
								<Link
									prefetch={false}
									href="https://www.iport.ru/sales/garantirovannaya-vygoda-do-5-000-po-treyd-in/"
									target="_blank"
									className={cl.link}>
									акции
								</Link>
							</Tooltip>
						</div>
					)}

					<div>
						<span className={cl.formTitle}>Выберите ваше устройство</span>
						<ProductTradeInCalculator
							productSections={product.SECTIONS || []}
							productPrice={product.PRICE.VALUE}
							basePrice={baseTradeInPrice}
							onChange={setTradeInPriceFromValues}
							onReset={() => setTradeInPriceFromValues(null)}
							setPrice={setPrice}
							initialValues={initialTradeInValues}
						/>
						<p className={cl.formTitle}>Контактные данные</p>
						<form onSubmit={handleSubmit(onFinish)} className={cl.form}>
							<div className={clsx(cl.form__item, cl.form__item_fio)}>
								<Controller
									name="FIO"
									control={control}
									rules={{
										required: 'Заполните поле',
										pattern: {
											value: /^[^-][а-яА-Я]*\s?[а-яА-Я]*?$/,
											message: 'Некорректный формат'
										}
									}}
									render={({ field }) => <Input {...field} label="Имя" isLoading={userInfoIsLoading} />}
								/>
								{errors.FIO && <span className={cl.form__error}>{errors.FIO.message}</span>}
							</div>

							<div className={clsx(cl.form__item, cl.form__item_phone)}>
								<RHFPhoneInput name="PHONE" control={control} error={errors.PHONE} isLoading={userInfoIsLoading} />
							</div>

							<div className={clsx(cl.form__item, cl.form__item_email)}>
								<RHFEmailInput name="EMAIL" control={control} error={errors.EMAIL} isLoading={userInfoIsLoading} />
							</div>

							<div className={clsx(cl.form__item, cl.form__item_payment)}>
								<Controller
									name="PAYMENT"
									control={control}
									rules={{
										required: 'Выберите способ оплаты'
									}}
									render={({ field }) => (
										<Select
											{...field}
											placeholder="Способ оплаты"
											adaptive
											options={paymentOptions}
											disabled={false}
											onChange={value => field.onChange(value)}
										/>
									)}
								/>
								{errors.PAYMENT && <span className={cl.form__error}>{errors.PAYMENT.message}</span>}
							</div>

							<div className={clsx(cl.form__item, cl.form__item_delivery)}>
								<Controller
									name="DELIVERY"
									control={control}
									rules={{
										required: 'Укажите способ получения'
									}}
									render={({ field }) => (
										<Select
											{...field}
											placeholder="Способ получения"
											adaptive
											options={shopsList}
											disabled={false}
											onChange={value => field.onChange(value)}
										/>
									)}
								/>
								{errors.DELIVERY && <span className={cl.form__error}>{errors.DELIVERY.message}</span>}
							</div>

							<Button
								className={cl.form__submit}
								type="submit"
								isLoading={isLoading}
								disabled={isLoading || !tradeInPriceFromValues}>
								Отправить заявку
							</Button>
							<div className={cl.form__agreement}>
								<AgreementBlock />
							</div>
						</form>
					</div>
				</>
			) : (
				formResponse
			)}
		</Modal>
	)
}

type TradeInPriceFormValues = {
	TRADE_IN_ITEM: string
	TRADE_IN_PRICE: number
	ADDITIONAL_DISCOUNT: number
}

type ProductTradeInCalculatorProps = {
	onChange: (values: TradeInPriceFormValues) => void
	onReset: () => void
	setPrice: (price: number) => void
	productPrice: number
	basePrice: number
	productSections: string[]
	initialValues?: Record<string, string>
}

const getInitialTradeInCategory = (sections: string[] = [], categories: TradeinDevice[] = []): string => {
	const categoryCodes = sections.flatMap(section => SECTION_CATEGORY_CODES[section] || [])
	const matchedCategory = categoryCodes.find(code => categories.some(category => category.CODE === code))

	return matchedCategory || categories[0]?.CODE || 'iphone'
}

export const ProductTradeInCalculator = ({
	onChange,
	onReset,
	productSections,
	productPrice,
	basePrice,
	setPrice,
	initialValues
}: ProductTradeInCalculatorProps): JSX.Element => {
	const { data: categories } = useGetBaseCategoriesQuery()
	const [getPrice] = useLazyGetTradeInPriceQuery()
	const [activeCategory, setActiveCategory] = useState<string | null>(null)
	const [values, setValues] = useState<Record<string, string>>({})
	const initialValuesApplied = useRef(false)
	const { data: detailedCategories, isLoading } = useGetCategoriesQuery(
		{ category: activeCategory ?? '' },
		{ skip: !activeCategory }
	)

	const sortedProperties = useMemo(
		() => (detailedCategories ? sortTradeInProperties(detailedCategories) : []),
		[detailedCategories]
	)

	useEffect(() => {
		if (!categories) return

		initialValuesApplied.current = false
		setActiveCategory(getInitialTradeInCategory(productSections, categories))
	}, [categories, productSections])

	const resetCalculatedPrice = useCallback(() => {
		setPrice(basePrice)
		onReset()
	}, [onReset, basePrice, setPrice])

	const buildTradeInItem = useCallback(
		(currentValues: Record<string, string>) => {
			const activeCategoryLabel = categories?.find(category => category.CODE === activeCategory)?.NAME
			const selectedValues = sortedProperties.map(select => currentValues[select.CODE]).filter(Boolean)

			return [activeCategoryLabel, ...(selectedValues || [])].filter(Boolean).join(', ')
		},
		[activeCategory, categories, sortedProperties]
	)

	const handleSubmit = useCallback(
		async (currentValues: Record<string, string>) => {
			const payload = { LIST_SECTION_CODE: activeCategory, ...currentValues }
			const res = await getPrice(payload).unwrap()

			if (res) {
				// «Итого» = цена товара − выкуп по трейд-ин − гарантированная выгода по акции
				setPrice(getTradeInProductPrice(productPrice, res.PRICE + res.ADDITIONAL_DISCOUNT))
				onChange({
					TRADE_IN_PRICE: res.PRICE,
					ADDITIONAL_DISCOUNT: res.ADDITIONAL_DISCOUNT,
					TRADE_IN_ITEM: buildTradeInItem(currentValues)
				})
			}
		},
		[activeCategory, buildTradeInItem, getPrice, onChange, productPrice, setPrice]
	)

	useEffect(() => {
		if (!initialValues || !sortedProperties.length || initialValuesApplied.current) return

		const validCodes = new Set(sortedProperties.map(cat => cat.CODE))
		const filtered = Object.fromEntries(
			Object.entries(initialValues).filter(([key, value]) => validCodes.has(key) && value)
		)

		if (!Object.keys(filtered).length) return

		initialValuesApplied.current = true
		setValues(filtered)

		if (Object.keys(filtered).length === sortedProperties.length) {
			void handleSubmit(filtered)
		}
	}, [initialValues, sortedProperties, handleSubmit])

	const handleChangeOptions = useCallback(
		(option: string) => {
			const [name, value] = option.split('=')
			const newValues = getValuesAfterPropertyChange(sortedProperties, name, value, values)

			setValues(newValues)
			resetCalculatedPrice()

			if (sortedProperties.length && Object.keys(newValues).length === sortedProperties.length) {
				void handleSubmit(newValues)
			}
		},
		[sortedProperties, handleSubmit, resetCalculatedPrice, values]
	)

	const handleResetForm = useCallback(() => {
		setValues({})
		initialValuesApplied.current = false
		resetCalculatedPrice()
	}, [resetCalculatedPrice])

	const handleChangeCategories = useCallback(
		(value: string) => {
			setActiveCategory(value)
			handleResetForm()
			// Prevent initial values from being re-applied after manual category switch
			initialValuesApplied.current = true
		},
		[handleResetForm]
	)

	if (!categories || !activeCategory) return <Skeletons type={SkeletonType.TradeInCategories} />

	return (
		<form className={cl.tradeInCalculator}>
			<SegmentedControl
				options={categories.map(item => ({ label: item.NAME, value: item.CODE }))}
				value={activeCategory}
				onChange={handleChangeCategories}
				className={cl.tradeInCalculator__segment}
			/>
			<Select
				onChange={handleChangeCategories}
				className={cl.tradeInCalculator__categories}
				value={activeCategory}
				options={categories.map(item => ({ label: item.NAME, value: item.CODE }))}
			/>

			<div className={cl.tradeInCalculator__wrapper}>
				{sortedProperties.map((select, index) => (
					<TradeInPropertySelect
						key={select.CODE}
						property={select}
						index={index}
						sortedProperties={sortedProperties}
						values={values}
						category={activeCategory}
						onChange={handleChangeOptions}
						className={cl.tradeInCalculator__select}
					/>
				))}
			</div>

			<button onClick={handleResetForm} className={cl.tradeInCalculator__reset} type="button">
				<CrossIcon />
				Сбросить
			</button>

			{isLoading && <Skeletons type={SkeletonType.TradeInDevice} />}
		</form>
	)
}
